// Shared state for the assistant drawer.
//
// Any region on any screen can open the drawer with a scope. It lives at the
// Layout level so the panel opens IN PLACE - the whole point of the surface is
// that the user does not lose their position. Navigating to the assistant page
// is a choice ("open full view"), never the only path.
//
// The thread is deliberately kept here rather than inside the panel, so it
// survives the drawer being closed and reopened. It does NOT survive a page
// reload, which is the same session-scoped decision made for pins: a stored
// question can itself be PHI.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const AssistantDrawerContext = createContext(null);

export function AssistantDrawerProvider({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(null);
  const [thread, setThread] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  // Pins are SESSION ONLY - deliberately not persisted. A pinned question can
  // itself be PHI: "unreachable members on the Tier 3 worklist" describes a
  // cohort small enough to be identifying. Anything worth keeping leaves
  // through Copy, which carries its own provenance.
  const [pins, setPins] = useState([]);
  // Text to put in the ask box the next time the drawer renders (the command
  // bar's "Ask the assistant: <query>"). Prefilled only, never sent: the user
  // reads it and presses Ask. Held here rather than passed through the URL,
  // because a typed question can itself be PHI.
  const [draft, setDraft] = useState(null);

  // A pill is a claim about what is currently on screen. After navigating that
  // claim is false, so the scope clears - but the thread does not, because the
  // user has not finished thinking just because they changed page.
  useEffect(() => {
    setScope(null);
  }, [location.pathname]);

  // A pin stores the QUESTION AND ITS SCOPE, which is its identity. The answer
  // is cached alongside for display only - that is what makes staleness
  // computable rather than guessed.
  const addPin = useCallback((pin) => {
    setPins((current) => {
      if (current.some((p) => p.question === pin.question
          && (p.scope ? p.scope.region_id : null) === (pin.scope ? pin.scope.region_id : null))) {
        return current;
      }
      return current.concat([{
        id: (pin.question + ":" + Date.now()),
        question: pin.question,
        scope: pin.scope || null,
        answerText: pin.answerText || null,
        rowCount: typeof pin.rowCount === "number" ? pin.rowCount : null,
        value: pin.value || null,
        valueLabel: pin.valueLabel || null,
        rows: pin.rows || null,
        pinnedAt: new Date().toISOString(),
        // Snapshotted so drift is detectable. A pin is never silently
        // invalidated or silently refreshed - it is MARKED.
        matchedAtPin: pin.scope && typeof pin.scope.matched_count === "number"
          ? pin.scope.matched_count : null,
      }]);
    });
  }, []);

  const removePin = useCallback((id) => {
    setPins((current) => current.filter((p) => p.id !== id));
  }, []);

  const openWithScope = useCallback((nextScope) => {
    setScope(nextScope || null);
    setOpen(true);
  }, []);

  const openWithDraft = useCallback((text) => {
    setDraft(typeof text === "string" ? text : null);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({
    open,
    scope,
    thread,
    conversationId,
    openDrawer: () => setOpen(true),
    openWithScope,
    openWithDraft,
    draft,
    clearDraft: () => setDraft(null),
    closeDrawer: () => setOpen(false),
    clearScope: () => setScope(null),
    setThread,
    setConversationId,
    pins,
    addPin,
    removePin,
    // Recorded so the thread can show where each question was asked from.
    // Distinct from the pill: this is history and is never editable.
    currentPath: location.pathname,
  }), [open, scope, thread, conversationId, openWithScope, openWithDraft, draft, pins, addPin, removePin, location.pathname]);

  return (
    <AssistantDrawerContext.Provider value={value}>
      {children}
    </AssistantDrawerContext.Provider>
  );
}

// Returns null outside the provider rather than throwing, so a region rendered
// in isolation (a test, a storybook, a modal mounted elsewhere) degrades to no
// affordance instead of crashing the page.
export function useAssistantDrawer() {
  return useContext(AssistantDrawerContext);
}

export default AssistantDrawerContext;
